import React from "react";
import { MdAttachFile, MdClose, MdRefresh, MdSearch, MdSupportAgent } from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import { toastError, toastSuccess } from "lib/toast";
import DataTablePagination from "components/ui/DataTablePagination";
import { DataTableEmptyState, DataTableErrorState, DataTableLoadingState } from "components/ui/DataTableStates";
import {
  adminSupportRequestsService,
  type AdminSupportRequest,
} from "services/adminSupportRequestsService";
import SupportContactSettingsPanel from "./SupportContactSettingsPanel";

const statuses = ["", "pending", "in_progress", "resolved", "closed"] as const;

function statusLabel(status: string, isArabic: boolean): string {
  const labels: Record<string, [string, string]> = {
    pending: ["قيد المراجعة", "Pending"],
    in_progress: ["جاري العمل", "In progress"],
    resolved: ["تم الحل", "Resolved"],
    closed: ["مغلق", "Closed"],
  };
  return labels[status]?.[isArabic ? 0 : 1] ?? status;
}

function formatDate(value: string | null | undefined, isArabic: boolean): string {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat(isArabic ? "ar-EG" : "en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value.replace(" ", "T")));
  } catch {
    return value;
  }
}

function RequestDetails({
  request,
  isArabic,
  onClose,
  onSaved,
}: {
  request: AdminSupportRequest | null;
  isArabic: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [status, setStatus] = React.useState(request?.status ?? "pending");
  const [note, setNote] = React.useState(request?.admin_note ?? "");
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setStatus(request?.status ?? "pending");
    setNote(request?.admin_note ?? "");
  }, [request]);

  if (!request) return null;

  async function save() {
    setSaving(true);
    try {
      await adminSupportRequestsService.update(request.id, {
        status,
        admin_note: note.trim() || null,
      });
      toastSuccess(isArabic ? "تم تحديث طلب الدعم." : "Support request updated.");
      onSaved();
      onClose();
    } catch {
      toastError(isArabic ? "تعذر تحديث الطلب." : "Could not update the request.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4" role="presentation">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-white/10 bg-surface p-5 shadow-2xl sm:p-7" dir={isArabic ? "rtl" : "ltr"}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold text-brand-400">#{request.id}</p>
            <h2 className="mt-1 text-xl font-bold text-white">{isArabic ? "تفاصيل طلب الدعم" : "Support request details"}</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl p-2 text-slate-300 transition hover:bg-white/10" aria-label={isArabic ? "إغلاق" : "Close"}><MdClose className="h-5 w-5" /></button>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4"><p className="text-xs text-slate-400">{isArabic ? "الاسم" : "Name"}</p><p className="mt-1 font-semibold text-white">{request.name}</p></div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4"><p className="text-xs text-slate-400">{isArabic ? "رقم الهاتف" : "Phone"}</p><a href={`tel:${request.phone}`} className="mt-1 block font-semibold text-brand-300 hover:underline">{request.phone}</a></div>
        </div>
        <div className="mt-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4"><p className="text-xs text-slate-400">{isArabic ? "المشكلة" : "Problem"}</p><p className="mt-2 whitespace-pre-wrap text-sm leading-7 text-slate-100">{request.problem}</p></div>
        <p className="mt-3 text-xs text-slate-500">{formatDate(request.created_at, isArabic)}</p>

        {request.attachments?.length ? (
          <div className="mt-5">
            <p className="mb-2 text-sm font-bold text-white">{isArabic ? "المرفقات" : "Attachments"}</p>
            <div className="flex flex-wrap gap-2">
              {request.attachments.map((attachment) => attachment.url ? (
                <a key={attachment.id} href={attachment.url} target="_blank" rel="noreferrer" className="inline-flex max-w-full items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-xs text-brand-200 hover:bg-white/10"><MdAttachFile className="h-4 w-4 shrink-0" /><span className="truncate">{attachment.name}</span></a>
              ) : null)}
            </div>
          </div>
        ) : null}

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <label className="text-sm font-semibold text-slate-200">{isArabic ? "الحالة" : "Status"}
            <select value={status} onChange={(event) => setStatus(event.target.value)} className="ef-input-field mt-2">
              {statuses.slice(1).map((item) => <option key={item} value={item}>{statusLabel(item, isArabic)}</option>)}
            </select>
          </label>
          <label className="text-sm font-semibold text-slate-200">{isArabic ? "ملاحظة الأدمن" : "Admin note"}
            <textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} maxLength={5000} className="ef-input-field mt-2 resize-y" />
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-xl bg-white/10 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/15">{isArabic ? "إلغاء" : "Cancel"}</button>
          <button type="button" disabled={saving} onClick={() => void save()} className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-600 disabled:opacity-50">{saving ? "…" : isArabic ? "حفظ" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

export default function SupportRequestsPage() {
  const isArabic = useAppLanguage() === "ar";
  const [items, setItems] = React.useState<AdminSupportRequest[]>([]);
  const [selected, setSelected] = React.useState<AdminSupportRequest | null>(null);
  const [term, setTerm] = React.useState("");
  const [appliedTerm, setAppliedTerm] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [meta, setMeta] = React.useState({ current_page: 1, last_page: 1, total: 0, from: 0, to: 0 });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await adminSupportRequestsService.list({ page, limit: 15, term: appliedTerm || undefined, status: status || undefined });
      setItems(result.data);
      setMeta({
        current_page: result.meta.current_page,
        last_page: result.meta.last_page,
        total: result.meta.total,
        from: result.meta.from ?? 0,
        to: result.meta.to ?? 0,
      });
    } catch {
      setError(isArabic ? "تعذر تحميل طلبات الدعم." : "Could not load support requests.");
    } finally {
      setLoading(false);
    }
  }, [appliedTerm, isArabic, page, status]);

  React.useEffect(() => { void load(); }, [load]);

  return (
    <section dir={isArabic ? "rtl" : "ltr"} className="space-y-5">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-500/15 text-brand-300"><MdSupportAgent className="h-6 w-6" /></div>
          <h1 className="text-2xl font-black text-white sm:text-3xl">{isArabic ? "طلبات الدعم" : "Support requests"}</h1>
          <p className="mt-1 text-sm text-slate-400">{isArabic ? "مشاكل الدفع والطلبات المرسلة من الطلاب." : "Payment issues and requests sent by students."}</p>
        </div>
        <button type="button" onClick={() => void load()} className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10"><MdRefresh className="h-5 w-5" />{isArabic ? "تحديث" : "Refresh"}</button>
      </div>

      <SupportContactSettingsPanel isArabic={isArabic} />

      <div className="ef-panel flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1"><MdSearch className="pointer-events-none absolute top-1/2 start-3 h-5 w-5 -translate-y-1/2 text-slate-500" /><input value={term} onChange={(event) => setTerm(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { setPage(1); setAppliedTerm(term.trim()); } }} placeholder={isArabic ? "بحث بالاسم أو الهاتف أو المشكلة" : "Search by name, phone or problem"} className="ef-input-field ps-10" /></div>
        <select value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }} className="ef-input-field sm:max-w-[210px]">{statuses.map((item) => <option key={item} value={item}>{item ? statusLabel(item, isArabic) : isArabic ? "كل الحالات" : "All statuses"}</option>)}</select>
        <button type="button" onClick={() => { setPage(1); setAppliedTerm(term.trim()); }} className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-600">{isArabic ? "بحث" : "Search"}</button>
      </div>

      {loading ? <DataTableLoadingState message={isArabic ? "جارٍ تحميل الطلبات…" : "Loading requests…"} /> : error ? <DataTableErrorState message={error} isArabic={isArabic} onRetry={() => void load()} /> : items.length === 0 ? <DataTableEmptyState title={isArabic ? "لا توجد طلبات دعم" : "No support requests"} hint={isArabic ? "ستظهر هنا الرسائل الجديدة من صفحة التواصل." : "New messages from the contact page will appear here."} /> : (
        <div className="ef-panel overflow-hidden p-0">
          <div className="hidden grid-cols-[1.1fr_1fr_2fr_1fr_auto] gap-4 border-b border-white/10 bg-white/[0.04] px-5 py-4 text-xs font-bold text-slate-400 lg:grid"><span>{isArabic ? "الطالب" : "Student"}</span><span>{isArabic ? "الهاتف" : "Phone"}</span><span>{isArabic ? "المشكلة" : "Problem"}</span><span>{isArabic ? "الحالة" : "Status"}</span><span /></div>
          <div className="divide-y divide-white/10">
            {items.map((item) => (
              <button key={item.id} type="button" onClick={() => setSelected(item)} className="grid w-full gap-3 px-5 py-4 text-start transition hover:bg-white/[0.04] lg:grid-cols-[1.1fr_1fr_2fr_1fr_auto] lg:items-center lg:gap-4">
                <span className="min-w-0"><span className="block truncate font-bold text-white">{item.name}</span><span className="text-xs text-slate-500">#{item.id} · {formatDate(item.created_at, isArabic)}</span></span>
                <span className="text-sm text-slate-300">{item.phone}</span>
                <span className="line-clamp-2 text-sm text-slate-300">{item.problem}</span>
                <span><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${item.status === "resolved" ? "bg-emerald-500/15 text-emerald-300" : item.status === "closed" ? "bg-slate-500/20 text-slate-300" : item.status === "in_progress" ? "bg-amber-500/15 text-amber-300" : "bg-brand-500/15 text-brand-300"}`}>{statusLabel(item.status, isArabic)}</span></span>
                <span className="text-xs font-bold text-brand-300">{isArabic ? "عرض" : "View"} {item.attachments?.length ? `· ${item.attachments.length} 📎` : ""}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <DataTablePagination isArabic={isArabic} page={meta.current_page} totalPages={meta.last_page} fromItem={meta.from} toItem={meta.to} totalItems={meta.total} onPageChange={setPage} />
      <RequestDetails request={selected} isArabic={isArabic} onClose={() => setSelected(null)} onSaved={() => void load()} />
    </section>
  );
}
